import {
  createContext,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react'
import { noop } from 'utils/functions'
import { useTypedSelector } from 'redux/store'
import { currentProjectSelector } from 'redux/selectors'
import { hexToHSL } from 'utils/color'
import { useAppMode } from 'lib/mode/AppModeContext'

export type ThemeVariants = 'light' | 'dark'

const DEFAULT_COLOR_SCHEME: Record<string, string> = {
  '--brand-300': '#1fa2ff',
  '--brand-400': '#0189e9',
  '--color-brand-gray-dark-900': '#191919',
}

// All variables will now have hex + hsl variants when applied for project overrides.
// Namespaced variables use --proj-* prefix to avoid clobbering global tokens.
// We still set original names (non-namespaced) to maintain backward compatibility.
const NAMESPACE_PREFIX = '--proj-'

export interface ThemeProps {
  theme: ThemeVariants
  setTheme: Dispatch<SetStateAction<ThemeVariants>>
  isDark: boolean
}

const defaultSetTheme: ThemeProps['setTheme'] = noop
export const ThemeContext = createContext<ThemeProps>({
  theme: 'light',
  setTheme: defaultSetTheme,
  isDark: false,
})

export const ThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const initialState: ThemeVariants = getInitialTheme()
  const [theme, setTheme] = useState(initialState)
  const { isUnity } = useAppMode()

  const project = useTypedSelector(currentProjectSelector)

  // Track applied namespaced project variables so we can clean them up when project goes null.
  const appliedProjectVarsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (isUnity) return
    rawSetTheme(theme)
  }, [theme, project, isUnity])

  useEffect(() => {
    if (isUnity) return
    document.documentElement.dataset.theme = theme
  }, [theme, isUnity])

  useEffect(() => {
    if (isUnity) return
    const root = window.document.documentElement

    // If project is removed or has no color scheme, clean up namespaced variables and reset defaults.
    if (!project?.colorScheme) {
      appliedProjectVarsRef.current.forEach(v => root.style.removeProperty(v))
      appliedProjectVarsRef.current.clear()
      // Reapply defaults (only if changed) to ensure stable base.
      Object.entries(DEFAULT_COLOR_SCHEME).forEach(([key, value]) => {
        if (root.style.getPropertyValue(key) !== value) {
          root.style.setProperty(key, value)
        }
      })
      return
    }

    // Merge defaults with project overrides; we continue to set original variable names
    // so existing styles keep working, but also add namespaced + hsl variants for future usage.
    const colorScheme = project.colorScheme
    const merged = {
      ...DEFAULT_COLOR_SCHEME,
      ...colorScheme,
    }

    Object.entries(merged).forEach(([key, value]) => {
      if (!value) return
      // Apply only if changed (prevents unnecessary style recalcs)
      if (root.style.getPropertyValue(key) !== value) {
        root.style.setProperty(key, value)
      }

      // For project-provided overrides create namespaced variables.
      if (key in colorScheme) {
        const baseName = key.replace(/^--/, '')
        const nsVar = `${NAMESPACE_PREFIX}${baseName}`
        const hslValue = value.startsWith('#')
          ? hexToHSL(value)
          : value.includes('hsl')
            ? value
            : `/* Invalid color format: ${value} */`
        const nsHslVar = `${nsVar}-hsl`

        if (root.style.getPropertyValue(nsVar) !== value) {
          root.style.setProperty(nsVar, value)
        }
        if (root.style.getPropertyValue(nsHslVar) !== hslValue) {
          root.style.setProperty(nsHslVar, hslValue)
        }
        appliedProjectVarsRef.current.add(nsVar)
        appliedProjectVarsRef.current.add(nsHslVar)
      }
    })
  }, [project, isUnity])

  const isDark = theme === 'dark'

  const value: ThemeProps = useMemo(
    () => ({ theme, setTheme, isDark }),
    [theme, setTheme, isDark]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

const rawSetTheme = (rawTheme: string): void => {
  const root = window.document.documentElement
  const prevTheme = root.dataset.appTheme
  // Guard against removing unrelated classes: only remove if we previously set it.
  if (prevTheme && prevTheme !== rawTheme) {
    root.classList.remove(prevTheme)
  }
  if (!root.classList.contains(rawTheme)) root.classList.add(rawTheme)
  root.dataset.appTheme = rawTheme

  const isDark = rawTheme === 'dark'
  root.style.setProperty('color-scheme', isDark ? 'dark' : 'light')

  localStorage.setItem('color-theme', rawTheme)
}

const getInitialTheme = (): ThemeVariants => {
  const storedPrefs = window.localStorage.getItem('color-theme')
  if (
    storedPrefs != null &&
    (storedPrefs === 'light' || storedPrefs === 'dark')
  )
    return storedPrefs

  const userMedia = window.matchMedia('(prefers-color-scheme: dark)')
  if (userMedia.matches) return 'dark'

  return 'light'
}
